import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { TextField, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, OutlinedInput } from '@mui/material';
import { getTokenOHLCV } from 'services/portfolio';
import styles from './OHLCV.module.sass';
import { useAuth } from 'react-oidc-context';
import allAssetsImage from '../../assets/AT.png';
import Spinner from 'components/Spinner';

interface OHLCVChartProps {
  tokenAddress: string;
  title?: string;
}

type TimeFrame = '1h' | '4h' | '1d' | '1w';

interface ChartData {
  timestamp: string;
  tooltipTime: string;
  open: number;
  high: number;
  low: number;
  close: number;
  change?: number;
  changePercent?: number;
}

const OHLCVChart: React.FC<OHLCVChartProps> = ({ tokenAddress, title }) => {
  const auth = useAuth();
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Default to 7 days ago
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('4h'); // Changed default to 4h

  // Chart styles using CSS classes instead of inline styles
  const chartStyles = {
    text: { 
      fill: 'rgba(255, 255, 255, 0.7)', 
      fontSize: '0.75rem' 
    },
    grid: { 
      stroke: 'rgba(115, 246, 255, 0.05)' 
    }
  };

  // Submit handler for date range
  const handleSubmit = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    fetchData(); // Call fetchData directly when the Update button is clicked
  };

  const handleTimeFrameChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTimeFrame(event.target.value as TimeFrame);
    // Note: We don't call fetchData() here as we want updates to happen only on button click
  };

  // Function to fetch data (separated so it can be called on button click)
  const fetchData = async () => {
    if (!tokenAddress) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getTokenOHLCV({
        access_token: auth.user.access_token,
        token_address: tokenAddress,
        from_date: fromDate,
        to_date: toDate,
        time_frame: timeFrame // Using the timeFrame state
      });
      
      if (!response?.data?.result || response.data.result.length === 0) {
        setError('No data available for this token');
        return;
      }

      const sortedData = response.data.result.sort((a: any, b: any) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      const formattedData = sortedData.map((item, index, array) => {
        // Calculate change and change percent from previous candle
        const prevItem = index > 0 ? array[index - 1] : null;
        const change = prevItem ? Number(item.close) - Number(prevItem.close) : 0;
        const changePercent = prevItem ? (change / Number(prevItem.close)) * 100 : 0;
        
        return {
          timestamp: new Date(item.timestamp).toLocaleDateString(),
          tooltipTime: new Date(item.timestamp).toLocaleString(),
          open: Number(item.open),
          high: Number(item.high),
          low: Number(item.low),
          close: Number(item.close),
          change,
          changePercent
        };
      });
      
      setData(formattedData);
    } catch (err: any) {
      console.error('Detailed OHLCV error');
      setError(err.response?.data?.message || 'Failed to fetch chart data');
    } finally {
      setLoading(false);
    }
  };

  // Run the fetch on initial load and when tokenAddress changes
  useEffect(() => {
    fetchData();
  }, [tokenAddress]);

  // Dynamic formatting based on price range
  const formatPrice = (value: number) => {
    if (value < 0.0001) return value.toExponential(2);
    if (value < 0.01) return value.toFixed(6);
    if (value < 1) return value.toFixed(4);
    if (value < 1000) return value.toFixed(2);
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content} style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  const minPrice = Math.min(...data.map(d => d.low));
  const maxPrice = Math.max(...data.map(d => d.high));
  const priceBuffer = (maxPrice - minPrice) * 0.1;
  const yAxisDomain = [
    minPrice - priceBuffer,
    maxPrice + priceBuffer
  ];

  // Common input styles for all form controls
  const commonInputSx = {
    color: 'white',
    backgroundColor: 'rgba(23, 23, 30, 0.5)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(115, 246, 255, 0.6)',
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(115, 246, 255, 0.8)',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#73F6FF',
      borderWidth: '2px',
    }
  };
  
  const dateInputSx = {
    ...commonInputSx,
    '& input::-webkit-calendar-picker-indicator': {
      filter: 'invert(85%) sepia(28%) saturate(1175%) hue-rotate(152deg) brightness(104%) contrast(101%)'
    },
    '& .MuiInputBase-input': {
      color: 'white'
    },
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(23, 23, 30, 0.5)'
    }
  };
  
  const selectSx = {
    ...commonInputSx,
    '& .MuiSelect-icon': {
      color: 'rgba(115, 246, 255, 0.8)'
    }
  };
  
  // Add styling for the dropdown menu
  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: 'rgba(23, 23, 30, 0.95)',
        color: 'white',
        border: '1px solid rgba(115, 246, 255, 0.3)',
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'rgba(115, 246, 255, 0.1)'
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: 'rgba(115, 246, 255, 0.2)'
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: 'rgba(115, 246, 255, 0.3)'
        }
      }
    }
  };
  
  const labelStyles = { 
    color: 'rgba(115, 246, 255, 0.8)',
    background: 'rgba(23, 23, 30, 0.9)',
    padding: '0 4px'
  };

  return (
    <div className={styles.container}>
      {/* Date range summary removed as requested */}
      
      <div className={styles.controls}>
        <div className={styles.control}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="from-date-label" style={labelStyles}>From</InputLabel>
            <OutlinedInput
              id="from-date-input"
              type="date"
              value={tempFromDate}
              onChange={(e) => setTempFromDate(e.target.value)}
              label="From"
              sx={{
                ...dateInputSx,
                backgroundColor: 'rgba(23, 23, 30, 0.5)',
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                  backgroundColor: 'rgba(23, 23, 30, 0.5)'
                }
              }}
            />
          </FormControl>
        </div>
        
        <div className={styles.control}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="to-date-label" style={labelStyles}>To</InputLabel>
            <OutlinedInput
              id="to-date-input"
              type="date"
              value={tempToDate}
              onChange={(e) => setTempToDate(e.target.value)}
              label="To"
              sx={{
                ...dateInputSx,
                backgroundColor: 'rgba(23, 23, 30, 0.5)',
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                  backgroundColor: 'rgba(23, 23, 30, 0.5)'
                }
              }}
            />
          </FormControl>
        </div>
        
        <div className={styles.control}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="timeframe-select-label" style={labelStyles}>Time Frame</InputLabel>
            <Select
              labelId="timeframe-select-label"
              value={timeFrame}
              label="Time Frame"
              onChange={handleTimeFrameChange}
              sx={selectSx}
              MenuProps={menuProps}
            >
              <MenuItem value="1h">1 Hour</MenuItem>
              <MenuItem value="4h">4 Hours</MenuItem>
              <MenuItem value="1d">1 Day</MenuItem>
              <MenuItem value="1w">1 Week</MenuItem>
            </Select>
          </FormControl>
        </div>
        
        <div className={styles.control}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleSubmit}
                          sx={{
              color: '#73F6FF',
              borderColor: 'rgba(115, 246, 255, 0.3)',
              backgroundColor: 'rgba(23, 23, 30, 0.5)',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
              height: '40px',
              marginTop: '5px'
            }}
          >
            Update
          </Button>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
          >
            <defs>
              <pattern id="backgroundImage" patternUnits="userSpaceOnUse" width="100%" height="100%">
                <image 
                  href={allAssetsImage}
                  x="27%"
                  y="15%"
                  width="45%"
                  height="45%"
                  opacity="0.07"
                  preserveAspectRatio="xMidYMid meet"
                  transform="translate(-30%, -30%)"
                />
              </pattern>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#73F6FF" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#73F6FF" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            
            {/* Background rectangle with image */}
            <rect 
              x="0" 
              y="0" 
              width="100%" 
              height="100%" 
              fill="url(#backgroundImage)" 
            />
            
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartStyles.grid.stroke}
              vertical={true}
            />
            <XAxis
              dataKey="timestamp"
              angle={-45}
              textAnchor="end"
              height={40}
              interval="preserveStartEnd"
              tick={chartStyles.text}
              axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              domain={yAxisDomain}
              tick={chartStyles.text}
              tickFormatter={formatPrice}
              axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
              tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(115, 246, 255, 0.05)' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const isPositive = data.close >= data.open;
                  
                  return (
                    <div className={styles.tooltip}>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Time:</span>
                        <span>{data.tooltipTime}</span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Open:</span>
                        <span className={styles.tooltipValue}>{formatPrice(data.open)}</span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>High:</span>
                        <span className={styles.tooltipValue}>{formatPrice(data.high)}</span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Low:</span>
                        <span className={styles.tooltipValue}>{formatPrice(data.low)}</span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Close:</span>
                        <span className={styles.tooltipValue}>{formatPrice(data.close)}</span>
                      </div>
                      {data.changePercent !== undefined && (
                        <div className={styles.tooltipRow}>
                          <span className={styles.tooltipLabel}>Change:</span>
                          <span style={{ color: isPositive ? '#26a69a' : '#ef5350' }}>
                            {data.changePercent > 0 ? '+' : ''}{data.changePercent.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={chartStyles.text}
            />
            <Bar
              yAxisId="left"
              dataKey="high"
              width={6}
              fill="none"
              shape={({ x, y, width, height, payload }) => {
                if (!payload) return null;

                const candleWidth = 6;
                const isGreen = payload.close > payload.open;
                const color = isGreen ? "#26a69a" : "#ef5350";

                const scale = height / (yAxisDomain[1] - yAxisDomain[0]);
                const openPos = (yAxisDomain[1] - payload.open) * scale;
                const closePos = (yAxisDomain[1] - payload.close) * scale;
                const highPos = (yAxisDomain[1] - payload.high) * scale;
                const lowPos = (yAxisDomain[1] - payload.low) * scale;

                return (
                  <g>
                    <line
                      x1={x + (candleWidth / 2)}
                      y1={y + highPos}
                      x2={x + (candleWidth / 2)}
                      y2={y + lowPos}
                      stroke={color}
                      strokeWidth={1}
                      strokeLinecap="round"
                    />
                    <rect
                      x={x}
                      y={y + Math.min(openPos, closePos)}
                      width={candleWidth}
                      height={Math.abs(openPos - closePos) || 1}
                      fill={color}
                      stroke={color}
                      strokeWidth={1}
                      rx={0.5}
                    />
                  </g>
                );
              }}
              name="Date"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OHLCVChart;
























